import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  RegionDto,
  RoleDto,
  UserDto,
  UserFullDto,
  UserFullUpdateDto,
  UserProvisionDto,
  UserRegionInterface,
  UserRoleInterface,
} from '@vatos-pas/common';
import { AppRole } from 'providers/roles';
import { getSupervisors } from 'services/regions';
import { axiosInstance } from 'services/http';
import { extractErrorMessage } from 'utils';

/**
 * User Container - Data Access Layer
 *
 * ARCHITECTURE NOTE: This container acts as a pluggable adapter between the
 * data layer and the module's components. Components (UserForm, views) remain
 * data-layer agnostic - they only consume this container's interface. This
 * allows swapping the data layer (e.g., react-admin, react-query, Apollo, or
 * plain fetch) without changing component code.
 *
 * SWR BEHAVIOR: Uses react-admin's useGetOne which caches data in Redux store.
 * Cached data is returned immediately while fresh data is fetched in the
 * background. Loading spinner only shows on first load when no cached data
 * exists. Cache persists across navigations via Redux.
 */

export interface RoleOption {
  id: string;
  name: string;
}

export interface RegionOption {
  id: string;
  name: string;
}

export interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  primaryPhone: string;
  idNumber: string;
  password?: string;
  active: boolean;
  selectedRoles: string[];
  selectedRegions: string[];
}

export interface SupervisorOption {
  id: string;
  name: string;
}

function useUser() {
  const dataProvider = useDataProvider();

  const [userId, setUserId] = useState<string>('');
  const {
    data: selectedUser,
    loading: userLoading,
    loaded: userLoaded,
    error: userError,
  } = useGetOne<UserDto>('user', userId, { enabled: !!userId });

  const isLoading = !!userId && !selectedUser && userLoading;
  const isRevalidating = !!userId && userLoading;

  const [formLoading, setFormLoading] = useState(false);
  const [supervisors, setSupervisors] = useState<SupervisorOption[]>([]);
  const [supervisorRoleId, setSupervisorRoleId] = useState<string>('');
  const [currentUserRegion, setCurrentUserRegion] =
    useState<UserRegionInterface | null>(null);
  const [userRoles, setUserRoles] = useState<UserRoleInterface[]>([]);
  const [userRegions, setUserRegions] = useState<UserRegionInterface[]>([]);
  const [linksError, setLinksError] = useState<string | null>(null);
  const [userRolesLoaded, setUserRolesLoaded] = useState(false);
  const [userRegionsLoaded, setUserRegionsLoaded] = useState(false);

  const loadUserById = useCallback((id: string): void => {
    setUserId(id);
  }, []);

  /**
   * One request: POST /user/provision persists the user, its roles and its
   * regions in a single API transaction (the old POST user → N POST
   * user-role → M POST user-region chain left role-less users behind when a
   * later call failed).
   */
  const createUser = useCallback(
    async (values: UserFormValues): Promise<UserFullDto> => {
      setFormLoading(true);
      try {
        const payload: UserProvisionDto = {
          user: {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            primaryPhone: values.primaryPhone,
            idNumber: values.idNumber,
            password: values.password ?? '',
            active: values.active,
          },
          roleIds: values.selectedRoles,
          regionIds: values.selectedRegions,
        };
        const result = await axiosInstance.post<UserFullDto>(
          'user/provision',
          payload,
        );
        setUserId(result.data.id);
        return result.data;
      } finally {
        setFormLoading(false);
      }
    },
    [],
  );

  /**
   * One request: PATCH /user/:id/full sends the editable user fields plus
   * the desired role and region sets; the API diffs them server-side.
   */
  const updateUser = useCallback(
    async (id: string, values: UserFormValues): Promise<UserFullDto> => {
      if (!selectedUser) {
        throw new Error('Cannot update: user data not loaded');
      }
      setFormLoading(true);
      try {
        const payload: UserFullUpdateDto = {
          user: {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            primaryPhone: values.primaryPhone,
            idNumber: values.idNumber,
            active: values.active,
          },
          roleIds: values.selectedRoles,
          regionIds: values.selectedRegions,
        };
        const result = await axiosInstance.patch<UserFullDto>(
          `user/${id}/full`,
          payload,
        );
        return result.data;
      } finally {
        setFormLoading(false);
      }
    },
    [selectedUser],
  );

  // loadOptions-compatible function for Autocomplete (lazy loading on focus)
  const loadRoleOptions = useCallback(
    async (_searchTerm: string): Promise<RoleOption[]> => {
      try {
        const result = await dataProvider.getList<RoleDto>('role', {
          filter: {},
          pagination: { page: 1, perPage: 200 },
          sort: { field: 'name', order: 'ASC' },
        });
        return result.data.map(role => ({
          id: role.id,
          name: role.name,
        }));
      } catch (error) {
        console.error('Failed to load roles:', error);
        return [];
      }
    },
    [dataProvider],
  );

  // loadOptions-compatible function for Autocomplete (lazy loading on focus)
  const loadRegionOptions = useCallback(
    async (_searchTerm: string): Promise<RegionOption[]> => {
      try {
        const result = await dataProvider.getList<RegionDto>('region', {
          filter: {},
          pagination: { page: 1, perPage: 999 },
          sort: { field: 'name', order: 'ASC' },
        });
        return result.data.map(region => ({
          id: region.id,
          name: region.name,
        }));
      } catch (error) {
        console.error('Failed to load regions:', error);
        return [];
      }
    },
    [dataProvider],
  );

  const findInactiveRegion =
    useCallback(async (): Promise<RegionOption | null> => {
      try {
        const result = await dataProvider.getList<RegionDto>('region', {
          filter: { 'name||$eq': 'Inactive User' },
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'name', order: 'ASC' },
        });
        if (result.data[0]) {
          return { id: result.data[0].id, name: result.data[0].name };
        }
        return null;
      } catch (error) {
        console.error('Failed to find inactive region:', error);
        return null;
      }
    }, [dataProvider]);

  const loadSupervisorsForRegion = useCallback(
    async (regionId: string): Promise<void> => {
      try {
        const response = await getSupervisors({ regionId });
        setSupervisors(
          response.data.data.map(({ id, firstName, lastName }) => ({
            id,
            name: `${firstName} ${lastName}`,
          })),
        );
      } catch (error) {
        console.error('Failed to load supervisors:', error);
        setSupervisors([]);
      }
    },
    [],
  );

  const loadSupervisorRoleId = useCallback(async (): Promise<void> => {
    try {
      const result = await dataProvider.getList<RoleDto>('role', {
        filter: {},
        pagination: { page: 1, perPage: 20 },
        sort: { field: 'id', order: 'ASC' },
      });

      const supervisorRole = result.data.find(
        role => role.name === AppRole.Supervisor,
      );
      if (supervisorRole) {
        setSupervisorRoleId(supervisorRole.id);
      }
    } catch (error) {
      console.error('Failed to load supervisor role ID:', error);
      setSupervisorRoleId('');
    }
  }, [dataProvider]);

  const loadUserRegion = useCallback(
    async (userId: string): Promise<void> => {
      try {
        const result = await dataProvider.getList<UserRegionInterface>(
          'user-region',
          {
            filter: { userId },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'id', order: 'ASC' },
          },
        );

        if (result.data[0]) {
          setCurrentUserRegion(result.data[0]);
        }
      } catch (error) {
        console.error('Failed to load user region:', error);
        setCurrentUserRegion(null);
      }
    },
    [dataProvider],
  );

  const loadUserRolesForUser = useCallback(
    async (userId: string): Promise<UserRoleInterface[]> => {
      try {
        const result = await dataProvider.getList<UserRoleInterface>(
          'user-role',
          {
            filter: { userId },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'id', order: 'DESC' },
          },
        );
        setUserRoles(result.data);
        setUserRolesLoaded(true);
        return result.data;
      } catch (error) {
        console.error('Failed to load user roles:', error);
        setUserRoles([]);
        setLinksError(extractErrorMessage(error, 'Failed to load user roles'));
        return [];
      }
    },
    [dataProvider],
  );

  const loadUserRegionsForUser = useCallback(
    async (userId: string): Promise<UserRegionInterface[]> => {
      try {
        const result = await dataProvider.getList<UserRegionInterface>(
          'user-region',
          {
            filter: { userId },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'id', order: 'DESC' },
          },
        );
        setUserRegions(result.data);
        setUserRegionsLoaded(true);
        return result.data;
      } catch (error) {
        console.error('Failed to load user regions:', error);
        setUserRegions([]);
        setLinksError(
          extractErrorMessage(error, 'Failed to load user regions'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  /**
   * The regional manager belongs to one user-region row (the region whose
   * managers the modal lists), so it is written there and nowhere else.
   */
  const updateUserRegionManager = useCallback(
    async (userRegionId: string, managerUserId: string): Promise<void> => {
      await dataProvider.update('user-region', {
        id: userRegionId,
        data: { managerUserId },
        previousData: { id: userRegionId },
      });
    },
    [dataProvider],
  );

  const resetLoadedFlags = useCallback(() => {
    setUserRolesLoaded(false);
    setUserRegionsLoaded(false);
    setLinksError(null);
  }, []);

  return {
    // Primary user state (SWR pattern via useGetOne + Redux)
    selectedUser,
    isLoading, // true only on first load (no cache in Redux)
    isRevalidating, // true during any fetch
    userLoaded, // true once fetch completed (use to detect "not found")
    userError, // truthy on fetch failure (404, network, etc.)
    // Secondary state
    formLoading,
    supervisors,
    supervisorRoleId,
    currentUserRegion,
    userRoles,
    userRegions,
    userRolesLoaded,
    userRegionsLoaded,
    linksError,
    // Actions
    loadUserById,
    createUser,
    updateUser,
    loadRoleOptions,
    loadRegionOptions,
    findInactiveRegion,
    loadSupervisorsForRegion,
    loadSupervisorRoleId,
    loadUserRegion,
    loadUserRolesForUser,
    loadUserRegionsForUser,
    updateUserRegionManager,
    setFormLoading,
    resetLoadedFlags,
  };
}

export const UserContainer = createContainer(useUser);
